import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useMutation } from 'react-query';
import { STORAGE_KEYS } from '../config';
import Logo from '../components/logo';
import { getUnixTime } from 'date-fns';
import { ref, push, child, update } from 'firebase/database';
import { analytics, db } from '../utils/firebase';
import { ClientWait } from '../types/types';
import useAuth from '../hooks/useAuth';
import { PatternFormat } from 'react-number-format';
import { logEvent, setUserProperties } from 'firebase/analytics';
import { ANALYTICS_EVENTS } from '../constants/analytics';
import httpClient from '../http-client';

function UserInfoPage() {
  const { companyId } = useParams();
  const [showError, setShowError] = useState(false);
  const [showNumberError, setShowNumberError] = useState(false);
  const [locationName, setLocationName] = useState<string | undefined>();
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    allowSms: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const { loading, userId, authUser } = useAuth();

  const inputStyle =
    'mb-5 block w-full p-4 text-sm text-gray-900 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500';
  const inputErrorStyle = showError && 'border-red-300';
  const inputNumberErrorStyle = showNumberError && 'border-red-300';

  const { mutate: addToWaitingList } = useMutation(
    async () => {
      const selectedService = window.sessionStorage.getItem(STORAGE_KEYS.SELECTED_SERVICE);
      const fcmToken = window.localStorage.getItem(STORAGE_KEYS.FCM_TOKEN);

      const userProperties = {
        name: formData.name,
        mobile_no: '+1' + formData.phoneNumber,
        allow_sms: formData.allowSms,
        company_id: companyId,
        service_type: selectedService!,
        user_id: userId,
        auth_user_ud: authUser!.uid
      };

      const newWait: ClientWait = {
        id: userProperties.user_id!,
        serviceType: userProperties.service_type!,
        // +1 for US phone number format
        // phoneNumber format: +1##########
        phoneNumber: userProperties.mobile_no,
        allowSms: userProperties.allow_sms,
        name: userProperties.name,
        date: getUnixTime(new Date()),
        fcmToken: fcmToken!,
      };

      const newWaitKey = push(child(ref(db), `WaitList/${companyId}`)).key;

      const updates: any = {};
      updates['/WaitList/' + companyId + '/' + newWaitKey] = newWait;

      await update(ref(db), updates);

      logEvent(analytics, ANALYTICS_EVENTS.USER_INFO_NEW_WALK_IN_SUCCESS, userProperties);

      setUserProperties(analytics, userProperties)

      return newWaitKey;
    },
    {
      onSuccess: (newWaitKey) => {
        navigate(`/${companyId}/checked-in?waitId=${newWaitKey}`);
      },
    }
  );

  useEffect(() => {
      if (companyId) {
        httpClient.get('/v1/getCompanyLocations/' + companyId).then((response) => {
          const locationName = response.data?.[0]?.Name;
          if (locationName) {
            setLocationName(locationName)
          }
        });
        
      }
    }, [companyId]);

  const handleSaveClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    if (formData.name === '' && formData.phoneNumber === '') {
      return setShowError(true);
    }
    if (formData.phoneNumber.length !== 10) {
      return setShowNumberError(true);
    }

    setShowNumberError(false);
    setShowError(false);
    saveUserInfo();
    setIsSubmitting(true);
    const selectedService = window.sessionStorage.getItem(STORAGE_KEYS.SELECTED_SERVICE);
    logEvent(analytics, ANALYTICS_EVENTS.USER_INFO_NEXT_PRESSED, {
      name: formData.name,
      mobile_no: '+1' + formData.phoneNumber,
      allow_sms: formData.allowSms,
      company_id: companyId,
      service_type: selectedService!,
      user_id: userId
    });
    addToWaitingList();
  };

  const saveUserInfo = () => {
    // store in localStroage so it persist to other location
    window.localStorage.setItem(STORAGE_KEYS.USER_NAME, formData.name);
    window.localStorage.setItem(STORAGE_KEYS.USER_PHONE, formData.phoneNumber);
  };

  const handleInputChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((data) => ({
      ...data,
      [target.name]: target.name === 'allowSms' ? !formData.allowSms : target.value,
    }));
  };

  // useEffect(() => {
  //   const name = window.localStorage.getItem(STORAGE_KEYS.USER_NAME) || '';
  //   const phoneNumber = window.localStorage.getItem(STORAGE_KEYS.USER_PHONE) || '';
  //   setFormData({
  //     name,
  //     phoneNumber,
  //     allowSms: true,
  //   });
  // }, []);

  return (
    <div className='flex justify-center flex-col items-center'>
      <Logo companyId={companyId!}></Logo>
      <div className='flex flex-col items-center w-full'>
        <p className='text-center px-5'>Enter your name and phone number</p>
        <hr className='h-px w-36 mx-auto border-0 my-4 bg-gray-300' />
        <p className='text-sm mb-10'>{locationName ? locationName : ''}</p>
        <form className='w-full px-10' autoComplete='off'>
          <input
            required
            name='name'
            onChange={handleInputChange}
            value={formData.name}
            className={twMerge(inputStyle, inputErrorStyle)}
            placeholder='Name'
          />
          <PatternFormat
            required
            type='tel'
            name='phoneNumber'
            className={twMerge(inputStyle, inputErrorStyle, inputNumberErrorStyle)}
            format='+1 (###) ###-####'
            value={formData.phoneNumber}
            valueIsNumericString={true}
            placeholder='Enter phone number'
            onValueChange={({ value }) => {
              setFormData((data) => ({
                ...data,
                phoneNumber: value,
              }));
            }}
          />
          <div className='flex items-center mb-10'>
            <input
              name='allowSms'
              checked={formData.allowSms}
              onChange={handleInputChange}
              id='allowSms'
              type='checkbox'
              className='w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 '
            />
            <label htmlFor='allowSms' className='ml-2 text-sm font-medium text-gray-900'>
              Allow for texts messages so your service provider can notify you when they are ready
              to see you
            </label>
          </div>
          {showError === true && (
            <div className='text-red-700 text-center text-2xl p-2.5'>
              Please enter name & phone number
            </div>
          )}
          <button
            className='bg-blue-700 w-full block text-white text-2xl p-2.5  px-6 mb-14 rounded-full'
            onClick={handleSaveClick}
            disabled={isSubmitting}
          >
            Next
          </button>
        </form>
      </div>
    </div>
  );
}

export default UserInfoPage;
